import { useCurrentPeriodFilter } from '@va/dashboard/util-hooks';
import { get, post } from '@va/http-client';
import { Where } from '@va/types/filters';
import { DateRange, PaginatedResponse, Sorting } from '@va/types/requests';
import { Pagination } from '@va/types/table';
import { useFetchDataParams, useLazyDataFetch } from '@va/util/hooks';
import { RequestCanceler, useCancelOnUnmount } from '@va/util/misc';
import { useCallback, useMemo } from 'react';

export type StaticPagesSearchResponse = { PageTotal: number; pageItems: PageItem[] };

type PageItem = {
  key: string;
  title: string;
  url: string;
  visits: string;
};

export const getPagesListSearchable = (
  websiteId: string,
  queryData?: Partial<{
    search: { general: string };
    start: number;
    length: number;
  }>,
) => {
  const url = `/websites/${websiteId}/pages-new`;
  return get<StaticPagesSearchResponse>(url, queryData);
};

export type PageAnalyticsDistributionItem = {
  value: string;
  sessionCount: number;
  percentOfTotal: number;
};

export type PageAnalyticsTrafficChannelItem = PageAnalyticsDistributionItem & {
  trafficChannelCategoryDistribution?: PageAnalyticsDistributionItem[];
};

type PageDimension = {
  pageUrl: string;
  pageUrlPathLevel1: string;
  pageUrlPathLevel2: string;
  pageUrlPathLevel3: string;
  pageUrlPathLevel4: string;
  pageUrlPathLevel5: string;
};

type PageMetricRequestMap = {
  sessionCount: number;
  previousSessionCount: number;
  sessionPercentOfTotal: number;
  sessionCountPercentageChange: number;

  visitCount: number;
  previousVisitCount: number;
  visitPercentOfTotal: number;
  visitCountPercentageChange: number;
  directVisitCount: number;

  visitorCount: number;
  newVisitorCount: number;
  returningVisitorCount: number;
  loyalVisitorCount: number;

  outgoingEventCount: number;
  bounceRate: number;
  advancedBounceRate: number;
  continuationRate: number;
  averageSessionDuration: number;
  averageVisitDuration: number;
  averageActiveViewportDuration: number | null;
  averageScrollDepth: number | null;
  topReferrerUrl: string;
  topReferrerVisitCount: number;

  countryDistribution: PageAnalyticsDistributionItem[];
  deviceDistribution: PageAnalyticsDistributionItem[];
  osDistribution: PageAnalyticsDistributionItem[];
  browserDistribution: PageAnalyticsDistributionItem[];
  referrerDistribution: PageAnalyticsDistributionItem[];
  trafficChannelDistribution: PageAnalyticsTrafficChannelItem[];
  trafficChannelCategoryDistribution: undefined; // Flag metric: populates categoryDistribution inside trafficChannelDistribution items
};

export type PageAnalyticsDimensions = keyof PageDimension | never;
export type PageAnalyticsMetrics = keyof PageMetricRequestMap;

export type PageAnalyticsResponse<M extends PageAnalyticsMetrics, D extends PageAnalyticsDimensions = never> = {
  pageTitle: string;
  segments: {
    id: string;
    name: string;
    data: PageAnalyticsResponse<M, D>;
  }[];
} & {
  [Key in D]: Key extends keyof PageDimension ? PageDimension[Key] : never;
} & {
  [Key in M]: PageMetricRequestMap[Key];
};

export type PageAnalyticsPayload<M extends PageAnalyticsMetrics[], D extends PageAnalyticsDimensions[] = never> = {
  pagination?: Pagination;
  dateRange: DateRange;
  previousDateRange?: DateRange;
  where?: Where;
  order?: Sorting<M[number]>[];
  metrics: M;
  dimensions?: D;
  segments?: Segment[];
};

type HelperParams<T> = {
  trace: string;
  controller?: AbortController;
  config?: useFetchDataParams<T>['config'];
};

export const fetchPageAnalytics = <M extends PageAnalyticsMetrics[], D extends PageAnalyticsDimensions[] = never>(
  websiteId: string,
  query: Record<string, string>,
  payload: PageAnalyticsPayload<M, D>,
  controller?: AbortController,
) => {
  return post<PaginatedResponse<PageAnalyticsResponse<M[number], D[number]>[]>>(
    `/v2/websites/${websiteId}/analytics/pages`,
    query,
    payload,
    { signal: controller?.signal },
  );
};

export type Segment = {
  id: string;
  name: string;
  conditions: Where;
};

export const PATH_LEVEL_DIMENSIONS = [
  'pageUrlPathLevel1',
  'pageUrlPathLevel2',
  'pageUrlPathLevel3',
  'pageUrlPathLevel4',
  'pageUrlPathLevel5',
] as const satisfies PageAnalyticsDimensions[];

export const usePageAnalytics = <
  M extends PageAnalyticsMetrics[],
  D extends PageAnalyticsDimensions[] = never,
  T = PaginatedResponse<PageAnalyticsResponse<M[number], D[number]>[]>,
>(
  {
    trace,
    controller,
    config,
    dateRange: dateRangeOverride,
    metrics,
    dimensions,
    order,
    pagination,
    where,
    previousDateRange,
    segments,
  }: Omit<PageAnalyticsPayload<M, D>, 'dateRange'> & HelperParams<T> & { dateRange?: DateRange; segments?: Segment[] },
  shouldFetch: boolean = true,
  mapper?: (response: PaginatedResponse<PageAnalyticsResponse<M[number], D[number]>[]>) => T | Promise<T>,
) => {
  const { from, until, websiteId } = useCurrentPeriodFilter();

  const payload = useMemo<PageAnalyticsPayload<M, D>>(
    () => ({
      metrics,
      dimensions,
      order,
      pagination,
      where,
      dateRange: dateRangeOverride ?? { start: from, end: until },
      previousDateRange: previousDateRange,
      segments: segments,
    }),
    [dateRangeOverride, dimensions, from, metrics, order, pagination, previousDateRange, segments, until, where],
  );

  const requestId = useMemo(() => RequestCanceler.generateId(), []);
  useCancelOnUnmount(requestId);

  const fetchFn = useCallback(
    async (_: string, websiteId: string, query: Record<string, string>, payload: PageAnalyticsPayload<M, D>) => {
      RequestCanceler.onRequestStart(requestId);
      const res = await fetchPageAnalytics(websiteId, query, payload, controller);
      RequestCanceler.onRequestEnd(requestId);
      return res;
    },
    [controller, requestId],
  );

  return useLazyDataFetch(
    shouldFetch ? [`/v2/websites/${websiteId}/analytics/pages`, websiteId, { trace }, payload] : null,
    {
      ...config,
      onError: () => RequestCanceler.onRequestEnd(requestId),
    },
    mapper,
    fetchFn,
  );
};
